import * as Sentry from '@sentry/react'
import { createLogglyWriter } from './logglyWriter.client'
import omit from 'lodash/omit'
import { isClient } from 'utils/runtimeUtils'
import { errorToString } from 'utils/errorUtils'
import { redactSecretsForLog } from 'utils/logRedaction'

const LogLevels = {
  DEBUG: 'debug',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
}

const LOG_DEBUG_KEY = 'LOG_DEBUG'

// A client stuck in a retry loop (e.g. dead network) can emit hundreds of
// identical errors in one session, flooding Sentry/Loggly and tripping
// rate-based alerts. Repeats past the cap are demoted to warnings — invisible
// to Sentry and error-level alerts, still fully visible in Loggly. Client-only:
// the server process lives across requests, where a lifetime cap would demote
// real recurring errors.
const MAX_REPORTS_PER_ERROR_MESSAGE = 10

export class LoggerService {
  constructor() {
    this.debug = false
    this.didInit = false
    this.writers = []
    this.tags = {}
    this.errorReportCounts = new Map()
  }

  init(keys, userId) {
    if (this.didInit) {
      this.warning('Attempted to init logger after it was already initialized')
      return
    }

    this.userId = userId
    this.didInit = true

    if (isClient()) {
      try {
        const allowDebug = localStorage.getItem(LOG_DEBUG_KEY)
        this.debug = allowDebug === 'true'
      } catch (_e) {}
      this._initLogglyWriter(keys)
    }

    if (this.debug || keys.runtimeEnv !== 'production') {
      this._initConsoleWriter()
    }

    this.cache.forEach(({ level, message, metadata }) =>
      this._writeLog(level, message, metadata)
    )
    this.cache = []
  }

  _initLogglyWriter(keys) {
    const logglyApiKey = keys.logglyApiKey
    const logglySource = keys.logglySource

    if (logglyApiKey && keys.runtimeEnv !== 'local') {
      const logglyWriter = createLogglyWriter(logglyApiKey, logglySource)
      this.writers.push(logglyWriter)
    }
  }

  _initConsoleWriter() {
    this.writers.push(consoleWriter)
  }

  debug(message, metadata = {}) {
    if (this.debug) {
      this._writeLog(LogLevels.DEBUG, message, metadata)
    }
  }

  info(message, metadata = {}) {
    this._writeLog(LogLevels.INFO, message, metadata)
  }

  warning(message, metadata = {}) {
    this._writeLog(LogLevels.WARNING, message, metadata)
  }

  /**
   *
   * @param {string} message
   * @param error
   * @param metadata - Any additional data that we want to get reported alongside our error.
   * @param { Object } metadata.tags - Specific tags that will be reported to sentry for filtering and grouping purposes
   * @param { string[] } [metadata.fingerprint] - Replaces Sentry's stack-based grouping for this report
   * @param { 'error' | 'warning' } [metadata.level] - Sentry level of this report; defaults to error
   */
  error(message, error, metadata = {}) {
    const { tags, fingerprint, level } = metadata
    // The Loggly writer spreads the metadata over its payload, so a Sentry
    // level here would replace the log level.
    const writerMetadata = {
      ...omit(metadata, ['fingerprint', 'level']),
      error,
      errorMessage: error && errorToString(error),
    }
    if (isClient()) {
      const reportCount = (this.errorReportCounts.get(message) ?? 0) + 1
      this.errorReportCounts.set(message, reportCount)
      if (reportCount > MAX_REPORTS_PER_ERROR_MESSAGE) {
        this.warning(message, {
          ...writerMetadata,
          isDemotedRepeatingError: true,
        })
        return
      }
    }
    this._writeLog(LogLevels.ERROR, message, writerMetadata)
    // Wrap the original exception with a synthetic exception to allow
    // having the context (message) attached to the error
    const errorOptions = {
      ...(error ? { cause: error } : null),
    }
    const wrappedException = new Error(message, errorOptions)
    this._captureException(wrappedException, {
      extra: redactSecretsForLog(
        omit(metadata, ['tags', 'fingerprint', 'level'])
      ),
      tags,
      ...(fingerprint ? { fingerprint } : null),
      ...(level ? { level } : null),
    })
  }

  _captureException(exception, captureContext) {
    Sentry.captureException(exception, captureContext)
  }

  setTag(key, value) {
    this.tags[key] = value
  }

  cache = []

  _writeLog(level, message, metadata) {
    if (!this.didInit) {
      this.cache.push({ level, message, metadata })
      return
    }

    const params = redactSecretsForLog({
      ...metadata,
      userId: this.userId,
      ...this.tags,
    })

    this.writers.forEach((writer) => {
      writer.write(level, message, params)
    })
  }

  addWriter(writer) {
    this.writers.push(writer)
  }
}

const consoleWriter = {
  write: (level, message, metadata) => {
    switch (level) {
      case LogLevels.DEBUG:
        // eslint-disable-next-line no-console
        if (!window.KEYS.silent) console.debug(message, metadata)
        break
      case LogLevels.INFO:
        // eslint-disable-next-line no-console
        if (!window.KEYS.silent) console.info(message, metadata)
        break
      case LogLevels.WARNING:
        // eslint-disable-next-line no-console
        console.warn(message, metadata)
        break
      case LogLevels.ERROR:
        // eslint-disable-next-line no-console
        console.error(message, metadata.error, metadata)
        break
    }
  },
}
export const logger = new LoggerService()
